import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  getAppointments,
  updateAppointment,
  duplicateAppointment,
  getStaff,
  getAllStaffHours,
  getServices,
  getAllBlockedTimes,
  deleteBlockedTime,
} from "../lib/api.ts";
import type { WorkingHourRow } from "../components/MultiResourceCalendar.tsx";
import { buildStaffColorMap, StaffColor } from "../lib/staff-colors.ts";
import { useTheme } from "../lib/theme.tsx";
import { MultiResourceCalendar } from "../components/MultiResourceCalendar.tsx";
import { CalendarListView } from "../components/CalendarListView.tsx";
import { AgendaView } from "../components/AgendaView.tsx";
import { AppointmentDetailsModal } from "../components/AppointmentDetailsModal.tsx";
import { CheckoutModal } from "../components/CheckoutModal.tsx";
import { NewAppointmentModal } from "../components/NewAppointmentModal.tsx";
import { BlockTimeModal } from "../components/BlockTimeModal.tsx";
import { CalendarContextMenu, type ContextMenuItem } from "../components/CalendarContextMenu.tsx";
import { MonthCalendar } from "../components/MonthCalendar.tsx";
import { CalendarToolbar, type ViewPeriod, type ViewMode } from "../components/CalendarToolbar.tsx";
import { useCalendarPrefs } from "../lib/calendar-prefs.ts";

interface Appointment {
  id: string;
  start_at: string;
  end_at: string;
  status: string;
  customer_name: string;
  customer_email?: string | null;
  customer_phone?: string | null;
  service_name: string;
  service_ids?: string; // JSON array string, first id = primary service
  staff_id: string;
  staff_name: string;
  notes?: string | null;
}

interface StaffMember {
  id: string;
  name: string;
}

interface BlockedTime {
  id: string;
  staff_id: string;
  start_at: string;
  end_at: string;
  reason?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function toISO(d: Date): string {
  // Use local-timezone components (not UTC) so the date string matches what
  // the user sees in the calendar header. Otherwise viewing at e.g. 00:04
  // local would compute yesterday's UTC date and the API would return rows
  // for the wrong day.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Returns Monday-anchored week for a given date */
function getWeekDays(ref: Date): Date[] {
  const start = new Date(ref);
  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + diff);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

// ── WeekBar ──────────────────────────────────────────────────────────────────

function WeekBar({
  weekDays,
  selectedISO,
  onSelect,
  appointmentCounts,
}: {
  weekDays: Date[];
  selectedISO: string;
  onSelect: (iso: string) => void;
  appointmentCounts: Map<string, number>;
}) {
  const todayStr = toISO(new Date());

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: 4,
        marginBottom: "0.75rem",
      }}
    >
      {weekDays.map((day) => {
        const iso = toISO(day);
        const isToday = iso === todayStr;
        const isSelected = iso === selectedISO;
        const count = appointmentCounts.get(iso) ?? 0;

        return (
          <button
            key={iso}
            onClick={() => onSelect(iso)}
            className={isSelected ? "zd-day selected" : "zd-day"}
            aria-pressed={isSelected}
          >
            <div className="zd-day-dow">
              {day.toLocaleDateString(undefined, { weekday: "short" })}
            </div>
            <div className="zd-day-num">{day.getDate()}</div>
            {(isToday || count > 0) && (
              <div
                className="zd-day-todaybar"
                style={count > 0 && !isToday ? { background: "var(--ink-faint)" } : undefined}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Main Calendar page ───────────────────────────────────────────────────────

export default function Calendar() {
  const { slug } = useParams<{ slug: string }>();
  const { theme } = useTheme();

  // View state
  const [viewPeriod, setViewPeriod] = useState<ViewPeriod>("day");
  const [viewMode, setViewMode] = useState<ViewMode>("resource");
  const [calPrefs, updateCalPrefs] = useCalendarPrefs(slug);

  // Keyboard navigation. Skip if a form input has focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable) {
          return;
        }
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case "t":
        case "T":
          setCurrentDate(new Date());
          break;
        case "ArrowLeft":
          setCurrentDate((d) => {
            const next = new Date(d);
            const step = viewPeriod === "week" ? 7 : viewPeriod === "month" ? 0 : 1;
            if (viewPeriod === "month") next.setMonth(next.getMonth() - 1);
            else next.setDate(next.getDate() - step);
            return next;
          });
          break;
        case "ArrowRight":
          setCurrentDate((d) => {
            const next = new Date(d);
            const step = viewPeriod === "week" ? 7 : viewPeriod === "month" ? 0 : 1;
            if (viewPeriod === "month") next.setMonth(next.getMonth() + 1);
            else next.setDate(next.getDate() + step);
            return next;
          });
          break;
        case "d":
        case "D":
          setViewPeriod("day");
          break;
        case "w":
        case "W":
          setViewPeriod("week");
          break;
        case "m":
        case "M":
          setViewPeriod("month");
          break;
        case "a":
        case "A":
          setViewPeriod("agenda");
          break;
        case "+":
        case "=": {
          const next = calPrefs.zoom === 1 ? 2 : calPrefs.zoom === 2 ? 4 : 4;
          if (next !== calPrefs.zoom) updateCalPrefs({ zoom: next });
          break;
        }
        case "-":
        case "_": {
          const next = calPrefs.zoom === 4 ? 2 : calPrefs.zoom === 2 ? 1 : 1;
          if (next !== calPrefs.zoom) updateCalPrefs({ zoom: next });
          break;
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewPeriod, calPrefs.zoom, updateCalPrefs]);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Data state
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [staffColorMap, setStaffColorMap] = useState<Map<string, StaffColor>>(new Map());
  const [staffHours, setStaffHours] = useState<Map<string, WorkingHourRow[]>>(new Map());
  const [serviceAccents, setServiceAccents] = useState<Map<string, string>>(new Map());
  const [appointmentCounts, setAppointmentCounts] = useState<Map<string, number>>(new Map());
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>([]);
  const [selectedApptId, setSelectedApptId] = useState<string | null>(null);
  const [checkoutApptId, setCheckoutApptId] = useState<string | null>(null);
  const [quickAddPrefill, setQuickAddPrefill] = useState<
    | { date: string; time: string; endTime?: string; staffId: string }
    | null
  >(null);
  const [blockPrefill, setBlockPrefill] = useState<
    { date: string; time: string; staffId: string } | null
  >(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    items: ContextMenuItem[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // For month view: selected date to show detail below
  const [selectedMonthDate, setSelectedMonthDate] = useState<string | null>(null);

  // Derived values
  const currentISO = toISO(currentDate);
  const weekDays = getWeekDays(currentDate);

  // Build staffColorMap whenever staff or theme changes
  useEffect(() => {
    const ids = staff.map((s) => s.id);
    setStaffColorMap(buildStaffColorMap(ids, theme));
  }, [staff, theme]);

  // Fetch staff once
  useEffect(() => {
    if (!slug) return;
    getStaff(slug)
      .then((data: StaffMember[]) => setStaff(Array.isArray(data) ? data : []))
      .catch(() => setStaff([]));
  }, [slug]);

  // Fetch services for per-service accent colors
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    getServices(slug)
      .then((rows: Array<{ id: string; accent_hex?: string | null }>) => {
        if (cancelled || !Array.isArray(rows)) return;
        const m = new Map<string, string>();
        for (const r of rows) {
          if (typeof r.accent_hex === "string" && r.accent_hex) m.set(r.id, r.accent_hex);
        }
        setServiceAccents(m);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Fetch working hours for all staff in one bulk call.
  useEffect(() => {
    if (!slug) {
      setStaffHours(new Map());
      return;
    }
    let cancelled = false;
    getAllStaffHours(slug)
      .then((rows: unknown) => {
        if (cancelled) return;
        const list: WorkingHourRow[] = Array.isArray(rows) ? (rows as WorkingHourRow[]) : [];
        const m = new Map<string, WorkingHourRow[]>();
        for (const row of list) {
          const arr = m.get(row.staff_id) ?? [];
          arr.push(row);
          m.set(row.staff_id, arr);
        }
        setStaffHours(m);
      })
      .catch(() => {
        if (!cancelled) setStaffHours(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Fetch blocked times for all staff in one bulk call, scoped to the visible
  // range. We pass the current week's bounds — generous enough for Day, Week,
  // Agenda; Month is handled separately when needed.
  const loadBlocks = useCallback(async () => {
    if (!slug) {
      setBlockedTimes([]);
      return;
    }
    try {
      const range = {
        range_start: weekDays[0].toISOString(),
        range_end: new Date(weekDays[6].getTime() + 24 * 60 * 60 * 1000).toISOString(),
      };
      const rows = await getAllBlockedTimes(slug, range);
      setBlockedTimes(Array.isArray(rows) ? (rows as BlockedTime[]) : []);
    } catch {
      setBlockedTimes([]);
    }
    // weekDays is derived from currentDate; depend on the day-stamps directly
    // so we don't refetch on every re-render that produces a new array ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, weekDays[0].getTime(), weekDays[6].getTime()]);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  useEffect(() => {
    const handler = () => {
      load();
      loadBlocks();
    };
    window.addEventListener("zedule:refresh", handler);
    return () => window.removeEventListener("zedule:refresh", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadBlocks]);

  const removeBlock = async (block: BlockedTime) => {
    if (!slug) return;
    try {
      await deleteBlockedTime(slug, block.staff_id, block.id);
      setBlockedTimes((prev) => prev.filter((b) => b.id !== block.id));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Compute params for the current view
  const weekStartISO = toISO(weekDays[0]);
  const weekEndISO = toISO(weekDays[6]);

  // Fetch appointments whenever date/period changes
  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);

    let params: Record<string, string> = {};

    if (viewPeriod === "day") {
      params = { date: currentISO };
    } else if (viewPeriod === "week" || viewPeriod === "agenda") {
      params = { start_date: weekStartISO, end_date: weekEndISO };
    } else {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      params = { start_date: toISO(start), end_date: toISO(end) };
    }

    getAppointments(slug, params)
      .then((data: unknown) => {
        const arr: Appointment[] = Array.isArray(data) ? (data as Appointment[]) : [];
        setAppointments(arr);

        // Build count map for week bar + month calendar
        const countMap = new Map<string, number>();
        for (const a of arr) {
          const d = a.start_at.split("T")[0];
          countMap.set(d, (countMap.get(d) ?? 0) + 1);
        }
        setAppointmentCounts(countMap);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug, viewPeriod, currentISO, weekStartISO, weekEndISO, currentDate.getFullYear(), currentDate.getMonth()]);

  useEffect(() => {
    load();
  }, [load]);

  // Actions
  const doAction = async (id: string, status: string) => {
    if (!slug) return;
    try {
      await updateAppointment(slug, id, { status });
      setAppointments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status } : a))
      );
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Week bar: selecting a day in week mode navigates to that day
  function handleWeekBarSelect(iso: string) {
    setCurrentDate(new Date(iso + "T12:00:00"));
    if (viewPeriod === "week") setViewPeriod("day");
  }

  // Month view: selecting a date shows detail
  function handleMonthDateSelect(iso: string) {
    setSelectedMonthDate(iso || null);
  }

  // Appointments filtered for selected month date
  const selectedMonthAppts = selectedMonthDate
    ? appointments.filter((a) => a.start_at.startsWith(selectedMonthDate))
    : [];

  // Appointments for current day
  const dayAppts = appointments.filter((a) => a.start_at.startsWith(currentISO));

  // Blocked times overlapping the current day (any staff)
  const dayBlocks = blockedTimes.filter((b) => {
    const startDay = b.start_at.split("T")[0];
    const endDay = b.end_at.split("T")[0];
    return startDay <= currentISO && endDay >= currentISO;
  });

  return (
    <div>
      <CalendarToolbar
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        viewPeriod={viewPeriod}
        setViewPeriod={setViewPeriod}
        viewMode={viewMode}
        setViewMode={setViewMode}
        zoom={calPrefs.zoom}
        setZoom={(z) => updateCalPrefs({ zoom: z })}
        loading={loading}
        onRefresh={load}
        onClearMonthSelection={() => setSelectedMonthDate(null)}
        appointmentCounts={appointmentCounts}
        weekStart={calPrefs.weekStart}
      />

      {/* Error banner */}
      {error && (
        <div
          style={{
            background: "var(--status-cancelled-bg)",
            color: "var(--status-cancelled)",
            borderRadius: "8px",
            padding: "0.75rem 1rem",
            fontSize: "0.875rem",
            marginBottom: "1rem",
          }}
        >
          {error}
        </div>
      )}

      {/* ── WeekBar (Day + Week period) ── */}
      {(viewPeriod === "day" || viewPeriod === "week") && (
        <WeekBar
          weekDays={weekDays}
          selectedISO={currentISO}
          onSelect={handleWeekBarSelect}
          appointmentCounts={appointmentCounts}
        />
      )}

      {/* ── Content area ── */}
      {loading && appointments.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            color: "var(--text-muted)",
            fontSize: "0.875rem",
          }}
        >
          Loading...
        </div>
      ) : (
        <>
          {/* Day + Resource view */}
          {viewPeriod === "day" && viewMode === "resource" && (
            <MultiResourceCalendar
              appointments={dayAppts}
              staff={staff}
              staffColorMap={staffColorMap}
              date={currentISO}
              blockedTimes={dayBlocks}
              staffHours={staffHours}
              serviceAccents={serviceAccents}
              zoom={calPrefs.zoom}
              formatPrefs={calPrefs}
              onSelectAppt={setSelectedApptId}
              onSwipeDay={(delta) => {
                setCurrentDate((d) => {
                  const next = new Date(d);
                  next.setDate(next.getDate() + delta);
                  return next;
                });
              }}
              onAppointmentRightClick={({ id, pageX, pageY }) => {
                const appt = appointments.find((a) => a.id === id);
                if (!appt) return;
                const isOpen = appt.status === "confirmed";
                const items: ContextMenuItem[] = [
                  { label: "View details", onClick: () => setSelectedApptId(id) },
                  {
                    label: "Mark complete",
                    disabled: !isOpen,
                    onClick: () => doAction(id, "completed"),
                  },
                  {
                    label: "Mark no-show",
                    disabled: !isOpen,
                    onClick: () => doAction(id, "no_show"),
                  },
                  {
                    label: "Copy customer info",
                    onClick: () => {
                      const text = [
                        appt.customer_name,
                        appt.customer_phone ?? "",
                        appt.customer_email ?? "",
                      ]
                        .filter(Boolean)
                        .join("\n");
                      navigator.clipboard?.writeText(text).catch(() => {});
                    },
                  },
                  {
                    separatorAbove: true,
                    label: "Cancel booking",
                    variant: "danger",
                    disabled: !isOpen,
                    onClick: () => doAction(id, "cancelled"),
                  },
                ];
                setContextMenu({ x: pageX, y: pageY, items });
              }}
              onEmptyCellClick={setQuickAddPrefill}
              onEmptyCellRightClick={({ date, time, staffId, pageX, pageY }) => {
                const items: ContextMenuItem[] = [
                  {
                    label: "New appointment",
                    onClick: () => setQuickAddPrefill({ date, time, staffId }),
                  },
                  {
                    label: "Block this time",
                    onClick: () => setBlockPrefill({ date, time, staffId }),
                  },
                ];
                setContextMenu({ x: pageX, y: pageY, items });
              }}
              onReschedule={async (id, patch) => {
                if (!slug) return;
                // Optimistic update — apply the patch in local state, rollback on failure.
                const prev = appointments;
                setAppointments((curr) =>
                  curr.map((a) => (a.id === id ? { ...a, ...patch } : a)),
                );
                try {
                  await updateAppointment(slug, id, patch);
                  // Refresh from server to get any server-side adjustments (e.g.,
                  // staff_name on the cross-staff move).
                  load();
                } catch (err) {
                  setAppointments(prev);
                  setError((err as Error).message ?? "Could not update appointment");
                }
              }}
              onDuplicate={async (id, patch) => {
                if (!slug) return;
                try {
                  await duplicateAppointment(slug, id, patch);
                  load();
                } catch (err) {
                  setError((err as Error).message ?? "Could not duplicate appointment");
                }
              }}
              onDeleteBlock={removeBlock}
            />
          )}

          {/* Day + List view */}
          {viewPeriod === "day" && viewMode === "list" && (
            <CalendarListView
              appointments={dayAppts}
              staff={staff}
              staffColorMap={staffColorMap}
              date={currentISO}
              onAction={doAction}
            />
          )}

          {/* Week view: list view per day */}
          {viewPeriod === "week" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              {weekDays.map((day) => {
                const iso = toISO(day);
                const dayApts = appointments.filter((a) => a.start_at.startsWith(iso));
                const isToday = iso === toISO(new Date());
                if (dayApts.length === 0) return null;

                return (
                  <div key={iso}>
                    <div
                      style={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: isToday ? "var(--accent)" : "var(--text-muted)",
                        marginBottom: "0.5rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      {day.toLocaleDateString(undefined, {
                        weekday: "long",
                        month: "short",
                        day: "numeric",
                      })}
                      {isToday && (
                        <span
                          style={{
                            fontSize: "0.65rem",
                            background: "var(--accent)",
                            color: "white",
                            borderRadius: "999px",
                            padding: "1px 6px",
                            fontWeight: 600,
                          }}
                        >
                          Today
                        </span>
                      )}
                    </div>
                    <CalendarListView
                      appointments={dayApts}
                      staff={staff}
                      staffColorMap={staffColorMap}
                      date={iso}
                      onAction={doAction}
                    />
                  </div>
                );
              })}
              {appointments.length === 0 && (
                <div
                  className="card"
                  style={{
                    padding: "3rem",
                    textAlign: "center",
                    color: "var(--text-muted)",
                    fontSize: "0.875rem",
                  }}
                >
                  No appointments this week.
                </div>
              )}
            </div>
          )}

          {/* Agenda view: flat chronological list across the current week */}
          {viewPeriod === "agenda" && (
            <AgendaView
              appointments={appointments}
              fromISO={weekStartISO}
              toISO={weekEndISO}
              staffColorMap={staffColorMap}
              onSelect={setSelectedApptId}
              formatPrefs={calPrefs}
            />
          )}

          {/* Month view */}
          {viewPeriod === "month" && (
            <div>
              <MonthCalendar
                date={currentDate}
                appointmentCounts={appointmentCounts}
                selectedDate={selectedMonthDate}
                onSelectDate={handleMonthDateSelect}
              />

              {/* Selected date detail */}
              {selectedMonthDate && (
                <div style={{ marginTop: "1.25rem" }}>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "var(--text-muted)",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {new Date(selectedMonthDate + "T12:00:00").toLocaleDateString(undefined, {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                  {selectedMonthAppts.length === 0 ? (
                    <div
                      className="card"
                      style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: "var(--text-muted)",
                        fontSize: "0.875rem",
                      }}
                    >
                      No appointments on this day.
                    </div>
                  ) : (
                    <CalendarListView
                      appointments={selectedMonthAppts}
                      staff={staff}
                      staffColorMap={staffColorMap}
                      date={selectedMonthDate}
                      onAction={doAction}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {selectedApptId &&
        slug &&
        (() => {
          const appt = appointments.find((a) => a.id === selectedApptId);
          if (!appt) return null;
          return (
            <AppointmentDetailsModal
              appt={appt}
              slug={slug}
              onClose={() => setSelectedApptId(null)}
              onAction={async (id, status) => {
                await doAction(id, status);
              }}
              onUpdated={load}
              formatPrefs={calPrefs}
              onCollectPayment={() => setCheckoutApptId(appt.id)}
            />
          );
        })()}

      {checkoutApptId && slug && (
        <CheckoutModal
          slug={slug}
          appointmentId={checkoutApptId}
          onClose={() => setCheckoutApptId(null)}
          onDone={load}
        />
      )}

      {quickAddPrefill && slug && (
        <NewAppointmentModal
          slug={slug}
          defaultDate={quickAddPrefill.date}
          defaultTime={quickAddPrefill.time}
          defaultStaffId={quickAddPrefill.staffId}
          onClose={() => setQuickAddPrefill(null)}
          onCreated={() => {
            setQuickAddPrefill(null);
            load();
          }}
        />
      )}

      {blockPrefill && slug && (
        <BlockTimeModal
          slug={slug}
          defaultDate={blockPrefill.date}
          defaultStartTime={blockPrefill.time}
          defaultStaffId={blockPrefill.staffId}
          onClose={() => setBlockPrefill(null)}
          onCreated={() => {
            setBlockPrefill(null);
            loadBlocks();
          }}
        />
      )}

      {contextMenu && (
        <CalendarContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
}
